import Image from 'next/image';
import Link from 'next/link';

export interface CasinoPartner {
    name: string;
    code: string;
    img: string;
    url: string;
    featured: string;
    mainBonus: string;
    bonusDetail: string;
    extraBonus: string;
    bonusExample?: string;
    highlights: string[];
}

// Shared partner/bonus offer card — used on the homepage (Partners) and the bonuses page.
export default function CasinoPartnerCard({ casino }: { casino: CasinoPartner }) {
    return (
        <div className="relative backdrop-blur rounded-2xl overflow-hidden transition-all duration-300 group">
            {/* Animated glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10 p-4 sm:p-5 md:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-center">

                    {/* Left Side - Logo & Featured */}
                    <div className="lg:col-span-3 flex flex-col items-center lg:items-start text-center lg:text-left">
                        <div className="relative w-32 h-10 mb-3 transition-transform duration-300 group-hover:scale-110">
                            <Image
                                src={casino.img}
                                alt={casino.name}
                                fill
                                className="object-contain object-center lg:object-left"
                            />
                        </div>
                        <div className="inline-block bg-green-500/10 rounded-lg px-2.5 py-1 mb-1.5">
                            <p className="text-[10px] font-mono text-green-400 uppercase tracking-wider">Featured Offer</p>
                        </div>
                        <p className="text-xs font-mono text-gray-300 leading-relaxed">{casino.featured}</p>
                    </div>

                    {/* Center - Main Bonus */}
                    <div className="lg:col-span-5 text-center">
                        <div className="bg-gradient-to-br from-emerald-600/20 via-emerald-800/20 to-gray-900/20 rounded-xl p-4 transform transition-all duration-300 group-hover:shadow-lg group-hover:shadow-green-500/20">
                            <div className="mb-2">
                                <span className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-green-300 via-emerald-400 to-green-500 bg-clip-text text-transparent inline-block leading-none">
                                    {casino.mainBonus}
                                </span>
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-base md:text-lg font-mono font-bold text-white">{casino.bonusDetail}</p>
                                <p className="text-green-400 font-mono text-sm">{casino.extraBonus}</p>
                                {casino.bonusExample && (
                                    <p className="text-[11px] text-gray-400 font-mono mt-2 pt-2 border-t border-green-400/20">
                                        Example: {casino.bonusExample}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Promo Code & CTA */}
                    <div className="lg:col-span-4 flex flex-col items-center lg:items-stretch gap-3">
                        <div className="text-center lg:text-left">
                            <p className="text-[10px] text-gray-400 font-mono uppercase mb-1.5">Use Promo Code</p>
                            <div className="inline-block bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg px-5 py-2">
                                <p className="text-xl font-mono font-bold text-green-400 tracking-widest">{casino.code}</p>
                            </div>
                        </div>

                        <Link
                            href={casino.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-center bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 transition-all duration-200 px-6 py-2.5 font-mono font-bold text-gray-200 relative overflow-hidden"
                        >
                            <span className="relative z-10">CLAIM NOW →</span>
                            <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-green-400/30 to-green-400/0 transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        </Link>
                    </div>
                </div>

                {/* Bottom - Highlights */}
                <div className="mt-4 pt-4 border-t border-green-400/20">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2">
                        {casino.highlights.map((highlight, idx) => (
                            <div key={idx} className="flex items-start text-left gap-2 group/item">
                                <svg
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                    className="w-3.5 h-3.5 mt-[3px] shrink-0 text-green-400 transition-transform duration-200 group-hover/item:scale-125"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
                                    />
                                </svg>
                                <span className="text-xs font-mono text-gray-300 leading-5">{highlight}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
